import { useEffect, useRef } from 'react';
import './AudioMeter.css';

const BARS = 14;

/**
 * Индикатор уровня микрофона. Считает RMS по временнóй области —
 * это ближе к воспринимаемой громкости, чем пик по спектру.
 */
export function AudioMeter({ stream, active }: { stream: MediaStream | null; active: boolean }) {
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const host = hostRef.current;
    const track = stream?.getAudioTracks()[0];

    if (!host || !track || !active) {
      host?.style.setProperty('--level', '0');
      return;
    }

    const AudioCtx = window.AudioContext ?? (window as any).webkitAudioContext;
    if (!AudioCtx) return;

    const context: AudioContext = new AudioCtx();
    // Без жеста пользователя контекст стартует приостановленным, и анализатор
    // молча отдавал бы нули — индикатор выглядел бы сломанным.
    if (context.state === 'suspended') context.resume().catch(() => {});

    const source = context.createMediaStreamSource(new MediaStream([track]));
    const analyser = context.createAnalyser();
    analyser.fftSize = 1024;
    analyser.smoothingTimeConstant = 0.75;
    source.connect(analyser);

    const buffer = new Float32Array(analyser.fftSize);
    let frame = 0;
    let smoothed = 0;

    const tick = () => {
      analyser.getFloatTimeDomainData(buffer);

      let sum = 0;
      for (let i = 0; i < buffer.length; i++) sum += buffer[i] * buffer[i];
      const rms = Math.sqrt(sum / buffer.length);

      // Нормальная речь даёт RMS около 0.05–0.2 — растягиваем этот диапазон.
      const level = Math.min(1, rms * 6);
      // Быстрый подъём, медленный спад: индикатор не «дёргается».
      smoothed = level > smoothed ? level : smoothed * 0.86 + level * 0.14;

      host.style.setProperty('--level', smoothed.toFixed(3));
      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frame);
      source.disconnect();
      context.close().catch(() => {});
    };
  }, [stream, active]);

  return (
    <div
      ref={hostRef}
      className={`meter ${active ? '' : 'meter--off'}`}
      role="meter"
      aria-label="Уровень микрофона"
    >
      {Array.from({ length: BARS }, (_, i) => (
        <span key={i} className="meter__bar" style={{ '--i': i / (BARS - 1) } as React.CSSProperties} />
      ))}
    </div>
  );
}
